import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Nav } from "../components/Nav";
import { http, errMsg } from "../lib/api";
import { StepDevice } from "../components/request/StepDevice";
import { StepProblem } from "../components/request/StepProblem";
import { StepHandoff } from "../components/request/StepHandoff";
import { StepContact } from "../components/request/StepContact";
import { RequestSuccess } from "../components/request/RequestSuccess";

const STEPS = [
  { t: "Your machine", C: StepDevice },
  { t: "The fault", C: StepProblem },
  { t: "Hand-off", C: StepHandoff },
  { t: "Your details", C: StepContact },
];

const empty = { device_type: "", brand_model: "", problem: "", photos: [], handoff: "", address: "", name: "", email: "", phone: "", preferred_contact: "email" };

const validate = (step, f) => {
  const e = {};
  if (step === 0) {
    if (!f.device_type) e.device_type = "Choose laptop or desktop";
    if (f.brand_model.trim().length < 2) e.brand_model = "Enter the brand and model";
  }
  if (step === 1 && f.problem.trim().length < 10) e.problem = "Please describe the problem (at least 10 characters)";
  if (step === 2) {
    if (!f.handoff) e.handoff = "Choose pick up or posting";
    if (f.handoff === "collection" && f.address.trim().length < 5) e.address = "Enter your pick-up address";
  }
  if (step === 3) {
    if (f.name.trim().length < 2) e.name = "Enter your name";
    if (!/^\S+@\S+\.\S+$/.test(f.email)) e.email = "Enter a valid email";
    if (f.phone.replace(/\D/g, "").length < 6) e.phone = "Enter a valid phone number";
  }
  return e;
};

export default function RequestRepair() {
  const [step, setStep] = useState(0);
  const [f, setF] = useState(empty);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const next = async () => {
    const e = validate(step, f);
    setErrors(e);
    if (Object.keys(e).length) return;
    if (step < STEPS.length - 1) return setStep(step + 1);
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => k !== "photos" && fd.append(k, v));
      f.photos.forEach((p) => fd.append("photos", p));
      const { data } = await http.post("/repairs", fd);
      setDone({ ref: data?.ref, email: f.email });
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const { C } = STEPS[step];
  return (
    <div className="grain grid-lines relative min-h-screen">
      <Nav />
      <main className="relative mx-auto grid max-w-[1440px] gap-12 px-5 pb-24 pt-28 md:px-10 md:pt-36 lg:grid-cols-12">
        {done ? (
          <div className="lg:col-span-8"><RequestSuccess ref_={done.ref} email={done.email} /></div>
        ) : (
          <>
            <aside className="lg:col-span-4">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400">Repair request</p>
              <h1 className="mt-4 font-display text-4xl font-black uppercase leading-[0.92] tracking-tight sm:text-5xl lg:text-6xl">Tell us what broke.</h1>
              <ol className="mt-10 hidden space-y-1 lg:block">
                {STEPS.map((s, i) => (
                  <li key={s.t} data-testid={`step-indicator-${i}`} className={`flex items-center gap-4 border-l-2 py-3 pl-4 transition-colors duration-300 ${i === step ? "border-ember text-bone" : i < step ? "border-white/30 text-slate-300" : "border-white/10 text-slate-500"}`}>
                    <span className="font-mono text-xs">0{i + 1}</span>
                    <span className="font-display text-lg font-bold">{s.t}</span>
                  </li>
                ))}
              </ol>
            </aside>
            <section className="lg:col-span-8">
              <div className="mb-8 flex items-center justify-between font-mono text-xs uppercase tracking-[0.2em] text-slate-400">
                <span data-testid="step-counter">Step 0{step + 1} / 04 — {STEPS[step].t}</span>
              </div>
              <div className="mb-10 h-px w-full bg-white/10">
                <motion.div className="h-px bg-ember" animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} />
              </div>
              <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}>
                <C f={f} set={set} errors={errors} />
              </motion.div>
              <div className="mt-12 flex items-center justify-between gap-4">
                <button type="button" data-testid="request-back-btn" disabled={step === 0} onClick={() => setStep(step - 1)} className="inline-flex items-center gap-2 px-2 py-3 text-sm text-slate-300 hover:text-bone disabled:opacity-30">
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <button type="button" data-testid={step === STEPS.length - 1 ? "repair-form-submit-btn" : "request-next-btn"} onClick={next} disabled={busy} className="group inline-flex items-center gap-3 bg-ember px-7 py-4 font-semibold text-ink transition-[background-color,transform] duration-300 hover:bg-amber-400 active:scale-95 disabled:opacity-60">
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                  {step === STEPS.length - 1 ? "Submit request" : "Continue"}
                  {!busy && <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />}
                </button>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
